import React, { FC, useState } from 'react';
import { TrashIcon } from '../../../icons/OurIcons';

type Photo = {
  id: string;
  filename: string;
  content_type: string;
  image_url: string;
  created_at: string;
}

type PhotoHistoryListProps = {
  photos: Photo[];
  onSelectPhoto: (index: number) => void;
  onRemovePhoto: (photoId: string) => void;
}

const formatTimestamp = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
};

const PhotoHistoryList: FC<PhotoHistoryListProps> = ({ photos, onSelectPhoto, onRemovePhoto }) => {
  const [ view, setView ] = useState<'icon' | 'list'>('icon');

  if (photos.length === 0) {
    return (
      <div className='mt-4 rounded-xl bg-gray-100 p-6 text-center text-gray-400 text-sm'>
        No photos yet -- add some above.
      </div>
    );
  }

  return (
    <div className='mt-4'>
      <div className='flex items-center justify-between mb-2'>
        <h4 className='text-sm font-semibold text-gray-700'>Photo History</h4>
        <div className='flex rounded-md border border-gray-300 overflow-hidden text-sm'>
          <button
            type='button'
            onClick={() => setView('icon')}
            className={`px-3 py-1 ${view === 'icon' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600'}`}
          >
            Icons
          </button>
          <button
            type='button'
            onClick={() => setView('list')}
            className={`px-3 py-1 border-l border-gray-300 ${view === 'list' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600'}`}
          >
            List
          </button>
        </div>
      </div>

      {view === 'icon' ? (
        <div className='grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto'>
          {photos.map((photo, index) => (
            <div key={photo.id} className='relative group cursor-pointer' onClick={() => onSelectPhoto(index)}>
              <img src={photo.image_url} className='w-full h-24 object-cover rounded-md' />
              <button
                onClick={(e) => { e.stopPropagation(); onRemovePhoto(photo.id); }}
                className='absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity'
              >
                <TrashIcon height={14} width={14} />
              </button>
              <p className='text-xs text-gray-500 mt-1 truncate'>{formatTimestamp(photo.created_at)}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className='space-y-2 max-h-80 overflow-y-auto'>
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              className='flex items-center justify-between rounded-lg bg-gray-100 p-2 cursor-pointer hover:bg-gray-200'
              onClick={() => onSelectPhoto(index)}
            >
              <div className='flex items-center gap-3 min-w-0'>
                <img src={photo.image_url} className='w-12 h-12 object-cover rounded-md flex-shrink-0' />
                <div className='min-w-0'>
                  <p className='text-sm text-gray-900 truncate'>{photo.filename}</p>
                  <p className='text-xs text-gray-500'>Uploaded {formatTimestamp(photo.created_at)}</p>
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); onRemovePhoto(photo.id); }} className='flex-shrink-0 ml-2'>
                <TrashIcon height={14} width={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoHistoryList;
